import { asRecord, isRecord } from "@arti/utilities";

const USD_FORMATTER = new Intl.NumberFormat(undefined, {
  currency: "USD",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  style: "currency",
});

const USD_FORMATTER_PRECISE = new Intl.NumberFormat(undefined, {
  currency: "USD",
  maximumFractionDigits: 4,
  minimumFractionDigits: 2,
  style: "currency",
});

const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
  notation: "compact",
});

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function toFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function formatTokenHours(value: number | null): string {
  if (value === null) {
    return "\u2014";
  }
  return `${value.toLocaleString(undefined, {
    maximumFractionDigits: value >= 1000 ? 0 : 2,
  })} tok-h`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function formatUsd(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) {
    return "—";
  }

  if (Math.abs(value) > 0 && Math.abs(value) < 0.01) {
    return USD_FORMATTER_PRECISE.format(value);
  }

  return USD_FORMATTER.format(value);
}

export function formatTokenCount(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) {
    return "—";
  }

  if (value < 1000) {
    return Math.round(value).toLocaleString();
  }

  return COMPACT_NUMBER_FORMATTER.format(value);
}

/**
 * How a sub-1000 value renders in `formatAbbrevNumber`:
 *   - `exact`  → `String(value)` (assumes an integer; no rounding/separators)
 *   - `round`  → `String(Math.round(value))` (safe for fractional inputs)
 *   - `locale` → `value.toLocaleString()` (locale digit grouping)
 * Above 1000 all modes share the lowercase `1.2k` / `3.4M` form.
 */
export type AbbrevSmallStyle = "exact" | "round" | "locale";

/**
 * Lowercase-k/M abbreviation with one fixed decimal (e.g. `1.2k`, `3.4M`).
 * Distinct from `formatTokenCount` (Intl compact, uppercase `K`/`M`): this is
 * the long-standing blackboard card/stat style. The `small` mode controls only
 * the sub-1000 branch — pick per call site based on whether the input can be
 * fractional. Defaults to `locale`.
 */
export function formatAbbrevNumber(
  value: number,
  small: AbbrevSmallStyle = "locale",
): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  switch (small) {
    case "exact": {
      return String(value);
    }
    case "round": {
      return String(Math.round(value));
    }
    case "locale": {
      return value.toLocaleString();
    }
  }
}

/**
 * Compact k-only abbreviation for tight micro-labels (sparklines, axis ticks):
 * drops the decimal once into the tens-of-thousands (`9.9k` → `12k`) and has no
 * M tier. Sub-1000 rounds to an integer.
 */
export function formatTokensCompact(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(value >= 10_000 ? 0 : 1)}k`;
  }
  return String(Math.round(value));
}

export function formatTokensWithUnit(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) {
    return "—";
  }
  return `${formatTokenCount(value)} tok`;
}

export function getUsageSummaryTotalCostUsd(
  usageSummary: unknown,
): number | null {
  if (!isRecord(usageSummary)) {
    return null;
  }

  const summary = usageSummary;
  const totals = asRecord(summary.totals);
  const candidate =
    totals?.costUsd ??
    totals?.cost_usd ??
    summary.total_cost_usd ??
    summary.costUsd ??
    summary.cost_usd;

  return isFiniteNumber(candidate) ? candidate : null;
}
